"use strict";
/**
 * Utility functions for creating PostgreSQL timestamptz strings from various date/time formats
 *
 * @author Your Name
 * @version 1.0.0
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCurrentTimestampTZ = exports.formatTimestampTZForDisplay = exports.timestampTZToDate = exports.validateTimestampTZInputs = exports.isValidTimestampTZ = exports.createScheduledTimestamp = exports.createTimestampTZ = exports.TimestampTZError = void 0;
/**
 * Error types for better error handling
 */
class TimestampTZError extends Error {
    input;
    constructor(message, input) {
        super(message);
        this.input = input;
        this.name = "TimestampTZError";
    }
}
exports.TimestampTZError = TimestampTZError;
// ============================================
// MAIN FUNCTIONS
// ============================================
/**
 * Creates a timestamptz string from date, time, and UTC offset
 *
 * @param dateString - Date in various formats (YYYY-MM-DD, DD/MM/YYYY, etc.)
 * @param timeString - Time in various formats (HH:mm, H:mm AM/PM, etc.)
 * @param utcOffset - UTC offset as string ("+07:00", "+7", "UTC+7") or number (7, -8, 5.5)
 * @returns PostgreSQL timestamptz string (e.g., "2025-06-23T18:00:00+07:00")
 *
 * @example
 * ```typescript
 * createTimestampTZ("2025-06-23", "18:00", "+07:00")
 * // Returns: "2025-06-23T18:00:00+07:00"
 *
 * createTimestampTZ("23/06/2025", "6:00 PM", 7)
 * // Returns: "2025-06-23T18:00:00+07:00"
 * ```
 */
function createTimestampTZ(dateString, timeString, utcOffset) {
    try {
        const normalizedDate = normalizeDateString(dateString);
        const normalizedTime = normalizeTimeString(timeString);
        const normalizedOffset = normalizeUTCOffset(utcOffset);
        return `${normalizedDate}T${normalizedTime}${normalizedOffset}`;
    }
    catch (error) {
        if (error instanceof TimestampTZError) {
            throw error;
        }
        throw new TimestampTZError(`Failed to create timestamptz: ${error instanceof Error ? error.message : "Unknown error"}`, `${dateString} ${timeString} ${utcOffset}`);
    }
}
exports.createTimestampTZ = createTimestampTZ;
/**
 * Creates a scheduled timestamp relative to a base date
 *
 * @param baseDate - Base date in YYYY-MM-DD format
 * @param daysOffset - Number of days to offset (negative for days before, positive for days after, 0 for same day)
 * @param timeString - Time for the scheduled event
 * @param utcOffset - UTC offset for timezone
 * @param minutesOffset - Number of minutes to offset from the base time (optional, default: 0)
 * @returns PostgreSQL timestamptz string
 *
 * @example
 * ```typescript
 * // Reminder email 1 day before at 6 PM
 * createScheduledTimestamp("2025-02-03", -1, "18:00", "+07:00")
 * // Returns: "2025-02-02T18:00:00+07:00"
 *
 * // Thank you email same day at 4 PM + 2 hours (120 minutes)
 * createScheduledTimestamp("2025-02-03", 0, "16:00", "+07:00", 120)
 * // Returns: "2025-02-03T18:00:00+07:00"
 *
 * // Follow-up email 2 days after at 9 AM - 30 minutes
 * createScheduledTimestamp("2025-02-03", 2, "09:00", "+07:00", -30)
 * // Returns: "2025-02-05T08:30:00+07:00"
 * ```
 */
function createScheduledTimestamp(baseDate, daysOffset, timeString, utcOffset, minutesOffset = 0) {
    try {
        // Parse base date
        const [year, month, day] = baseDate.split("-").map(Number);
        if (!year ||
            !month ||
            !day ||
            year < 1000 ||
            month < 1 ||
            month > 12 ||
            day < 1 ||
            day > 31) {
            throw new TimestampTZError("Invalid base date format. Expected YYYY-MM-DD", baseDate);
        }
        // Calculate target date
        const targetDate = new Date(year, month - 1, day);
        targetDate.setDate(targetDate.getDate() + daysOffset);
        // Format target date
        const scheduledYear = targetDate.getFullYear();
        const scheduledMonth = String(targetDate.getMonth() + 1).padStart(2, "0");
        const scheduledDay = String(targetDate.getDate()).padStart(2, "0");
        const scheduledDate = `${scheduledYear}-${scheduledMonth}-${scheduledDay}`;
        // Parse and adjust time with minutes offset
        const adjustedTime = addMinutesToTime(timeString, minutesOffset);
        // If time adjustment caused day overflow, adjust the date as well
        if (adjustedTime.dayOffset !== 0) {
            targetDate.setDate(targetDate.getDate() + adjustedTime.dayOffset);
            // Recalculate date string after potential day change
            const finalYear = targetDate.getFullYear();
            const finalMonth = String(targetDate.getMonth() + 1).padStart(2, "0");
            const finalDay = String(targetDate.getDate()).padStart(2, "0");
            const finalDate = `${finalYear}-${finalMonth}-${finalDay}`;
            return createTimestampTZ(finalDate, adjustedTime.time, utcOffset);
        }
        return createTimestampTZ(scheduledDate, adjustedTime.time, utcOffset);
    }
    catch (error) {
        if (error instanceof TimestampTZError) {
            throw error;
        }
        throw new TimestampTZError(`Failed to create scheduled timestamp: ${error instanceof Error ? error.message : "Unknown error"}`, `${baseDate} + ${daysOffset} days at ${timeString} + ${minutesOffset} minutes`);
    }
}
exports.createScheduledTimestamp = createScheduledTimestamp;
/**
 * Adds minutes to a time string and handles day overflow
 * @param timeString - Time in HH:mm or HH:mm:ss format
 * @param minutesToAdd - Minutes to add (can be negative)
 * @returns Object with adjusted time and day offset
 */
function addMinutesToTime(timeString, minutesToAdd) {
    // Normalize the time string first
    const normalizedTime = normalizeTimeString(timeString);
    const [hours, minutes, seconds] = normalizedTime.split(":").map(Number);
    // Convert to total minutes from midnight
    let totalMinutes = hours * 60 + minutes;
    // Add the offset
    totalMinutes += minutesToAdd;
    // Handle day overflow/underflow
    let dayOffset = 0;
    while (totalMinutes >= 1440) {
        // 24 hours = 1440 minutes
        totalMinutes -= 1440;
        dayOffset += 1;
    }
    while (totalMinutes < 0) {
        totalMinutes += 1440;
        dayOffset -= 1;
    }
    // Convert back to hours and minutes
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    // Format the time
    const adjustedTime = `${newHours.toString().padStart(2, "0")}:${newMinutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    return {
        time: adjustedTime,
        dayOffset,
    };
}
/**
 * Normalize date string to YYYY-MM-DD format
 */
function normalizeDateString(dateString) {
    if (!dateString || typeof dateString !== "string") {
        throw new TimestampTZError("Date string is required and must be a string", dateString);
    }
    const cleaned = dateString.trim();
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
        return cleaned;
    }
    // Handle DD/MM/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleaned)) {
        const [day, month, year] = cleaned.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    // Handle MM-DD-YYYY format
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(cleaned)) {
        const [month, day, year] = cleaned.split("-");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    // Handle MM/DD/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleaned)) {
        const [month, day, year] = cleaned.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    // Handle YYYY/MM/DD format
    if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(cleaned)) {
        const [year, month, day] = cleaned.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    throw new TimestampTZError(`Unsupported date format: ${dateString}`, dateString);
}
/**
 * Normalize time string to HH:mm:ss format
 */
function normalizeTimeString(timeString) {
    if (!timeString || typeof timeString !== "string") {
        throw new TimestampTZError("Time string is required and must be a string", timeString);
    }
    const cleaned = timeString.trim().toLowerCase();
    // Handle AM/PM format
    if (cleaned.includes("am") || cleaned.includes("pm")) {
        const isPM = cleaned.includes("pm");
        const timeOnly = cleaned.replace(/[ap]m/g, "").trim();
        if (!/^\d{1,2}:\d{2}$/.test(timeOnly)) {
            throw new TimestampTZError(`Invalid AM/PM time format: ${timeString}`, timeString);
        }
        const [hours, minutes] = timeOnly.split(":").map(Number);
        if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
            throw new TimestampTZError(`Invalid time values in AM/PM format: ${timeString}`, timeString);
        }
        let hour24 = hours;
        if (isPM && hours !== 12)
            hour24 += 12;
        if (!isPM && hours === 12)
            hour24 = 0;
        return `${hour24.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:00`;
    }
    // Handle HH:mm:ss format (already correct)
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(cleaned)) {
        const [hours, minutes, seconds] = cleaned.split(":").map(Number);
        if (hours < 0 ||
            hours > 23 ||
            minutes < 0 ||
            minutes > 59 ||
            seconds < 0 ||
            seconds > 59) {
            throw new TimestampTZError(`Invalid time values: ${timeString}`, timeString);
        }
        return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
    // Handle HH:mm format (add seconds)
    if (/^\d{1,2}:\d{2}$/.test(cleaned)) {
        const [hours, minutes] = cleaned.split(":").map(Number);
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            throw new TimestampTZError(`Invalid time values: ${timeString}`, timeString);
        }
        return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:00`;
    }
    throw new TimestampTZError(`Unsupported time format: ${timeString}`, timeString);
}
/**
 * Normalize UTC offset to ±HH:mm format
 */
function normalizeUTCOffset(utcOffset) {
    if (utcOffset === null || utcOffset === undefined) {
        throw new TimestampTZError("UTC offset is required", String(utcOffset));
    }
    // Convert to string if it's a number
    const offsetStr = utcOffset.toString().trim();
    // Already in ±HH:mm format
    if (/^[+-]\d{2}:\d{2}$/.test(offsetStr)) {
        return offsetStr;
    }
    // Handle ±H format (single digit)
    if (/^[+-]?\d{1}$/.test(offsetStr)) {
        const num = parseInt(offsetStr);
        if (num < -12 || num > 14) {
            throw new TimestampTZError(`Invalid UTC offset range: ${offsetStr}`, offsetStr);
        }
        const sign = num >= 0 ? "+" : "-";
        const hours = Math.abs(num).toString().padStart(2, "0");
        return `${sign}${hours}:00`;
    }
    // Handle ±HH format (double digit)
    if (/^[+-]?\d{2}$/.test(offsetStr)) {
        const num = parseInt(offsetStr);
        if (num < -12 || num > 14) {
            throw new TimestampTZError(`Invalid UTC offset range: ${offsetStr}`, offsetStr);
        }
        const sign = num >= 0 ? "+" : "-";
        const hours = Math.abs(num).toString().padStart(2, "0");
        return `${sign}${hours}:00`;
    }
    // Handle ±H.5 format (half hour)
    if (/^[+-]?\d{1}\.5$/.test(offsetStr)) {
        const num = parseFloat(offsetStr);
        if (num < -12 || num > 14) {
            throw new TimestampTZError(`Invalid UTC offset range: ${offsetStr}`, offsetStr);
        }
        const sign = num >= 0 ? "+" : "-";
        const hours = Math.floor(Math.abs(num)).toString().padStart(2, "0");
        return `${sign}${hours}:30`;
    }
    // Handle ±HH.5 format (half hour)
    if (/^[+-]?\d{2}\.5$/.test(offsetStr)) {
        const num = parseFloat(offsetStr);
        if (num < -12 || num > 14) {
            throw new TimestampTZError(`Invalid UTC offset range: ${offsetStr}`, offsetStr);
        }
        const sign = num >= 0 ? "+" : "-";
        const hours = Math.floor(Math.abs(num)).toString().padStart(2, "0");
        return `${sign}${hours}:30`;
    }
    // Handle formats like "UTC+7", "GMT-5", etc.
    const match = offsetStr.match(/(?:UTC|GMT)?([+-]?\d{1,2}(?:\.\d+)?)/i);
    if (match) {
        return normalizeUTCOffset(match[1]);
    }
    throw new TimestampTZError(`Unsupported UTC offset format: ${offsetStr}`, offsetStr);
}
// ============================================
// VALIDATION FUNCTIONS
// ============================================
/**
 * Validates if a string is a valid timestamptz format
 */
function isValidTimestampTZ(timestamp) {
    const timestampTZRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/;
    return timestampTZRegex.test(timestamp);
}
exports.isValidTimestampTZ = isValidTimestampTZ;
/**
 * Validates input parameters before creating timestamptz
 */
function validateTimestampTZInputs(dateString, timeString, utcOffset) {
    const errors = [];
    try {
        normalizeDateString(dateString);
    }
    catch (error) {
        errors.push(`Invalid date: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
    try {
        normalizeTimeString(timeString);
    }
    catch (error) {
        errors.push(`Invalid time: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
    try {
        normalizeUTCOffset(utcOffset);
    }
    catch (error) {
        errors.push(`Invalid UTC offset: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
}
exports.validateTimestampTZInputs = validateTimestampTZInputs;
// ============================================
// UTILITY FUNCTIONS
// ============================================
/**
 * Converts a timestamptz string to a JavaScript Date object
 */
function timestampTZToDate(timestamptz) {
    if (!isValidTimestampTZ(timestamptz)) {
        throw new TimestampTZError(`Invalid timestamptz format: ${timestamptz}`, timestamptz);
    }
    return new Date(timestamptz);
}
exports.timestampTZToDate = timestampTZToDate;
/**
 * Formats a timestamptz for display in a specific timezone
 */
function formatTimestampTZForDisplay(timestamptz, displayTimezone = "Asia/Bangkok", locale = "en-US") {
    const date = timestampTZToDate(timestamptz);
    return date.toLocaleString(locale, {
        timeZone: displayTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
    });
}
exports.formatTimestampTZForDisplay = formatTimestampTZForDisplay;
/**
 * Gets the current time as a timestamptz in a specific timezone
 */
function getCurrentTimestampTZ(utcOffset) {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const normalizedOffset = normalizeUTCOffset(utcOffset);
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${normalizedOffset}`;
}
exports.getCurrentTimestampTZ = getCurrentTimestampTZ;
